import React, { useState, useRef } from 'react'
import { Modal, Button } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { POST } from '../../../../services/api'
import { isValidSVG, sanitizeSVG, customizeSVG } from './svgUtils'

interface SVGUploadModalProps {
  show: boolean
  onHide: () => void
  onIconUploaded: (newIcon: any) => void
  categories: any[]
}

export const SVGUploadModal: React.FC<SVGUploadModalProps> = ({ 
  show, 
  onHide, 
  onIconUploaded,
  categories 
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [svgContent, setSvgContent] = useState<string>('')
  const [iconDescription, setIconDescription] = useState<string>('')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [isUploading, setIsUploading] = useState<boolean>(false)
  const [conversionProgress, setConversionProgress] = useState<string>('')
  
  // Customização de cores
  const [strokeWidth, setStrokeWidth] = useState<number>(1)
  const [strokeColor, setStrokeColor] = useState<string>('#000000')
  const [fillColor, setFillColor] = useState<string>('transparent')
  const [backgroundColor, setBackgroundColor] = useState<string>('transparent')
  
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    
    if (!file) return

    // Aceitar SVG e outros formatos de imagem
    const acceptedTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']
    const svgExtensions = ['.svg']
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp']
    
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
    const isValidType = acceptedTypes.includes(file.type) || 
                       svgExtensions.includes(fileExtension) || 
                       imageExtensions.includes(fileExtension)

    if (!isValidType) {
      Swal.fire({
        icon: 'error',
        title: 'Arquivo inválido',
        text: 'Por favor, selecione arquivos de imagem (SVG, PNG, JPG, JPEG, GIF, WebP).'
      })
      return
    }

    setSelectedFile(file)
    setConversionProgress('Processando arquivo...')

    // Se for SVG, processar normalmente
    if (file.type === 'image/svg+xml' || fileExtension === '.svg') {
      processSVGFile(file)
    } else {
      // Converter outros formatos para SVG
      convertImageToSVG(file)
    }
  }

  const processSVGFile = (file: File) => {
    // IMPORTANTE: Ler SVG como texto para armazenar no banco como string
    // O banco de dados armazena SVGs como texto, não como arquivos binários
    // Exemplo: <svg width="21" height="27" viewBox="0 0 21 27" fill="none">...</svg>
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      
      // Validar se o conteúdo é um SVG válido
      if (!isValidSVG(content)) {
        Swal.fire({
          icon: 'error',
          title: 'SVG inválido',
          text: 'O arquivo selecionado não contém um SVG válido.',
        })
        setSelectedFile(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
        return
      }
      
      // Sanitizar o SVG
      const sanitized = sanitizeSVG(content)
      setSvgContent(sanitized)
      setConversionProgress('')
    }
    
    reader.onerror = () => {
      Swal.fire({
        icon: 'error',
        title: 'Erro na leitura',
        text: 'Erro ao ler o arquivo SVG.'
      })
      setConversionProgress('')
    }
    
    // Ler como texto, não como dataURL ou buffer
    reader.readAsText(file)
  }

  const convertImageToSVG = (file: File) => {
    setConversionProgress('Convertendo imagem para SVG...')
    
    const reader = new FileReader()
    reader.onload = (e) => {
      const img = new Image()
      img.onload = () => {
        setConversionProgress('Criando SVG...')
        
        try {
          // Definir dimensões máximas para otimização
          const maxSize = 256
          let { width, height } = img
          
          // Redimensionar se necessário mantendo proporção
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }

          // Criar canvas para processar a imagem
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          
          if (!ctx) {
            throw new Error('Não foi possível criar contexto do canvas')
          }

          canvas.width = width
          canvas.height = height
          
          // Desenhar imagem redimensionada
          ctx.drawImage(img, 0, 0, width, height)
          
          // Converter para base64
          const dataURL = canvas.toDataURL('image/png', 0.8)
          
          // Criar SVG com a imagem embarcada
          const svgContent = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
  <image href="${dataURL}" width="${width}" height="${height}"/>
</svg>`
          
          // Validar SVG criado
          if (!isValidSVG(svgContent)) {
            throw new Error('Erro na criação do SVG')
          }

          setSvgContent(svgContent)
          setConversionProgress('')
          
          Swal.fire({
            icon: 'success',
            title: 'Conversão concluída!',
            text: `Imagem ${file.name} convertida para SVG com sucesso.`,
            timer: 3000,
            showConfirmButton: false
          })
          
        } catch (error) {
          console.error('Erro na conversão:', error)
          setConversionProgress('')
          Swal.fire({
            icon: 'error',
            title: 'Erro na conversão',
            text: 'Não foi possível converter a imagem para SVG. Tente outro arquivo.'
          })
          setSelectedFile(null)
          if (fileInputRef.current) {
            fileInputRef.current.value = ''
          }
        }
      }
      
      img.onerror = () => {
        setConversionProgress('')
        Swal.fire({
          icon: 'error',
          title: 'Erro na imagem',
          text: 'Não foi possível carregar a imagem. Verifique se o arquivo não está corrompido.'
        })
        setSelectedFile(null)
        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }
      }
      
      img.src = e.target?.result as string
    }
    
    reader.onerror = () => {
      setConversionProgress('')
      Swal.fire({
        icon: 'error',
        title: 'Erro na leitura',
        text: 'Erro ao ler o arquivo de imagem.'
      })
    }
    
    reader.readAsDataURL(file)
  }

  const applySVGCustomizations = (svgString: string): string => {
    return customizeSVG(svgString, {
      strokeWidth,
      strokeColor: strokeColor !== 'transparent' ? strokeColor : undefined,
      fillColor: fillColor !== 'transparent' ? fillColor : 'transparent',
      backgroundColor: backgroundColor !== 'transparent' ? backgroundColor : undefined
    })
  }

  const handleUpload = async () => {
    if (!selectedFile || !iconDescription.trim() || !selectedCategory) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos obrigatórios',
        text: 'Por favor, preencha todos os campos obrigatórios.',
      })
      return
    }

    setIsUploading(true)

    try {
      // FLUXO DO UPLOAD:
      // 1. SVG já está em memória como string (carregado via FileReader.readAsText)
      // 2. Aplicamos customizações de cor/stroke (ainda em memória)
      // 3. Sanitizamos para segurança
      // 4. Enviamos string SVG para banco via API (não arquivo)
      
      // Aplicar customizações ao SVG
      const customizedSVG = applySVGCustomizations(svgContent)
      
      // Validação final: garantir que o SVG customizado ainda é válido
      if (!isValidSVG(customizedSVG)) {
        throw new Error('SVG tornou-se inválido após customizações')
      }
      
      // Preparar dados conforme SvgWebAppCreateDto
      // Backend DTO: { svg: string, description?: string, category: string }
      const uploadData = {
        svg: customizedSVG,  // String SVG pronta para armazenar no banco
        description: iconDescription.trim(),
        category: selectedCategory
      }

      // Fazer upload via API
      const response = await POST('/svgwebapp', uploadData)

      if (response && (response.success !== false)) {
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: 'Ícone SVG salvo com sucesso!',
        })

        // Chamar callback com o novo ícone
        onIconUploaded(response.data || response)
        
        // Limpar formulário
        resetForm()
        onHide()
      } else {
        throw new Error(response?.message || 'Erro ao salvar o ícone')
      }
    } catch (error: any) {
      console.error('Erro no upload:', error)
      Swal.fire({
        icon: 'error',
        title: 'Erro no upload',
        text: error.message || 'Erro ao fazer upload do ícone.',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const resetForm = () => {
    setSelectedFile(null)
    setSvgContent('')
    setIconDescription('')
    setSelectedCategory('')
    setConversionProgress('')
    setStrokeWidth(1)
    setStrokeColor('#000000')
    setFillColor('transparent')
    setBackgroundColor('transparent')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClose = () => {
    resetForm()
    onHide()
  }

  const previewSVG = svgContent ? applySVGCustomizations(svgContent) : ''

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Upload de Ícone (SVG/Imagem)</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="row">
          {/* Coluna esquerda - Formulário */}
          <div className="col-md-6">
            <div className="mb-4">
              <label className="form-label fw-bold">Arquivo de Imagem *</label>
              <input
                ref={fileInputRef}
                type="file"
                className="form-control"
                accept=".svg,.png,.jpg,.jpeg,.gif,.webp,image/svg+xml,image/png,image/jpeg,image/gif,image/webp"
                onChange={handleFileSelect}
              />
              <small className="form-text text-muted">
                Aceita SVG, PNG, JPG, JPEG, GIF e WebP. Imagens serão convertidas para SVG automaticamente.
              </small>
              {conversionProgress && (
                <div className="mt-2">
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                      <span className="visually-hidden">Processando...</span>
                    </div>
                    <small className="text-primary">{conversionProgress}</small>
                  </div>
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Descrição do Ícone *</label>
              <input
                type="text"
                className="form-control"
                value={iconDescription}
                onChange={(e) => setIconDescription(e.target.value)}
                placeholder="Ex: Ícone de telefone"
                maxLength={100}
              />
            </div>

            <div className="mb-4">
              <label className="form-label fw-bold">Categoria *</label>
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Selecione uma categoria</option>
                {categories.filter(cat => cat.value !== -1).map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.select}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Coluna direita - Customização e Preview */}
          <div className="col-md-6">
            <h6 className="fw-bold mb-3">Customização do SVG</h6>
            
            <div className="mb-3">
              <label className="form-label">Espessura da linha</label>
              <div className="d-flex align-items-center gap-2">
                <input
                  type="range"
                  className="form-range"
                  min="0.5"
                  max="5"
                  step="0.5"
                  value={strokeWidth}
                  onChange={(e) => setStrokeWidth(parseFloat(e.target.value))}
                />
                <span className="text-muted">{strokeWidth}px</span>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Cor da linha</label>
              <div className="d-flex gap-2">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={strokeColor}
                  onChange={(e) => setStrokeColor(e.target.value)}
                  style={{ width: '50px' }}
                />
                <input
                  type="text"
                  className="form-control"
                  value={strokeColor}
                  onChange={(e) => setStrokeColor(e.target.value)}
                  placeholder="#000000"
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Preenchimento</label>
              <div className="d-flex gap-2">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={fillColor === 'transparent' ? '#ffffff' : fillColor}
                  onChange={(e) => setFillColor(e.target.value)}
                  style={{ width: '50px' }}
                  disabled={fillColor === 'transparent'}
                />
                <select 
                  className="form-select"
                  value={fillColor}
                  onChange={(e) => setFillColor(e.target.value)}
                >
                  <option value="transparent">Transparente</option>
                  <option value="#000000">Preto</option>
                  <option value="#ffffff">Branco</option>
                  <option value="custom">Personalizada</option>
                </select>
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Fundo</label>
              <div className="d-flex gap-2">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={backgroundColor === 'transparent' ? '#ffffff' : backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                  style={{ width: '50px' }}
                  disabled={backgroundColor === 'transparent'}
                />
                <select
                  className="form-select"
                  value={backgroundColor}
                  onChange={(e) => setBackgroundColor(e.target.value)}
                >
                  <option value="transparent">Transparente</option>
                  <option value="#ffffff">Branco</option>
                  <option value="#f8f9fa">Cinza claro</option>
                  <option value="custom">Personalizada</option>
                </select>
              </div>
            </div>

            {/* Preview */}
            {previewSVG && (
              <div className="mt-4">
                <label className="form-label fw-bold">Preview Visual</label>
                <div 
                  className="border rounded p-3 text-center"
                  style={{ backgroundColor: '#f8f9fa', minHeight: '100px' }}
                >
                  <div
                    dangerouslySetInnerHTML={{ __html: previewSVG }}
                    style={{ maxWidth: '60px', maxHeight: '60px', margin: '0 auto' }}
                  />
                </div>
                
                <div className="mt-3">
                  <label className="form-label fw-bold">Código SVG que será salvo no banco</label>
                  <textarea
                    className="form-control"
                    value={previewSVG}
                    readOnly
                    rows={4}
                    style={{ fontSize: '11px', fontFamily: 'monospace' }}
                  />
                  <small className="text-muted">
                    Este é o código SVG que será armazenado como string no banco de dados
                  </small>
                  
                  {/* Informações do arquivo */}
                  {selectedFile && (
                    <div className="mt-3 p-2 bg-light rounded">
                      <small className="text-muted">
                        <strong>Arquivo original:</strong> {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)<br/>
                        <strong>Tipo:</strong> {selectedFile.type || 'Detectado pela extensão'}<br/>
                        <strong>SVG gerado:</strong> {(previewSVG.length / 1024).toFixed(1)} KB em texto
                      </small>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
          Cancelar
        </Button>
        {selectedFile && svgContent && (
          <Button 
            variant="outline-warning" 
            onClick={() => {
              resetForm()
              Swal.fire({
                icon: 'info',
                title: 'Formulário limpo',
                text: 'Selecione outro arquivo para converter.',
                timer: 2000,
                showConfirmButton: false
              })
            }} 
            disabled={isUploading}
          >
            Resetar
          </Button>
        )}
        <Button 
          variant="primary" 
          onClick={handleUpload} 
          disabled={!selectedFile || !iconDescription.trim() || !selectedCategory || isUploading || !!conversionProgress}
        >
          {isUploading ? 'Fazendo upload...' : conversionProgress ? 'Processando...' : 'Upload Ícone'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}